import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  fetchAllDues,
  fetchCurrentDuesPolicy,
  updateDuesPolicy,
} from '../../controllers/paymentsController';
import {
  DuesStatus,
  DuesStatusNames,
  BillingPeriodNames,
  getDuesStatusChipSx,
} from './duesConstants';
import { formatDuesDate, formatDuesMoney } from './duesFormUtils';
import CreateMemberDuesDialog from './components/CreateMemberDuesDialog';
import BulkCreateDuesDialog from './components/BulkCreateDuesDialog';
import { getCurrencyDisplayLabel, normalizeCurrencyInput } from '../../utils/currencyDisplay';

const Dues = () => {
  const navigate = useNavigate();
  const [dues, setDues] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(false);

  const [createSingleOpen, setCreateSingleOpen] = useState(false);
  const [createBulkOpen, setCreateBulkOpen] = useState(false);

  const loadDues = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, meta } = await fetchAllDues({
        pageNumber: page,
        pageSize,
        status: statusFilter !== '' ? Number(statusFilter) : null,
        searchTerm: searchTerm || null,
      });
      setDues(data || []);
      setTotalPages(meta?.totalPages || 1);
    } catch (e) {
      setError(e?.message || 'Aidatlar yüklenemedi');
      setDues([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    setPolicyLoading(true);
    try {
      const data = await fetchCurrentDuesPolicy();
      setPolicy(data);
    } catch (e) {
      setPolicy(null);
    } finally {
      setPolicyLoading(false);
    }
  };

  useEffect(() => {
    loadDues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, searchTerm]);

  const handleUpdatePolicy = async () => {
    if (!policy) return;
    setPolicyLoading(true);
    try {
      await updateDuesPolicy(policy);
      setPolicyDialogOpen(false);
      loadPolicy();
    } catch (e) {
      setError(e?.message || 'Politika güncellenemedi');
    } finally {
      setPolicyLoading(false);
    }
  };

  if (loading && dues.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={1}>
        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>Aidatlar</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => { loadPolicy(); setPolicyDialogOpen(true); }}
            sx={{ textTransform: 'none', borderColor: '#e6e6e6', color: '#444' }}
          >
            Aidat Politikası
          </Button>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => setCreateSingleOpen(true)}
            sx={{ textTransform: 'none', borderColor: '#e6e6e6', color: '#444' }}
          >
            Tek Üye
          </Button>
          <Button
            variant="contained"
            startIcon={<GroupAddIcon />}
            onClick={() => setCreateBulkOpen(true)}
            sx={{
              textTransform: 'none',
              backgroundColor: '#E7EDF4',
              boxShadow: 'none',
              color: '#0E4D94',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#CFDBEA', boxShadow: 'none' },
            }}
          >
            Toplu Oluştur
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ border: 'none', boxShadow: 'none', mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ minWidth: 250, flex: 1 }}>
            <Typography sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px' }}>Ara (Üye Adı)</Typography>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              placeholder="Üye adı ile ara..."
            />
          </Box>
          <Box sx={{ minWidth: 180 }}>
            <Typography sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px' }}>Durum</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <MenuItem value="">Tümü</MenuItem>
              {Object.entries(DuesStatusNames).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Box>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.05)', border: '1px solid #f5f5f5', py: 1, px: 2.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Üye</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Dönem</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Tutar</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Vade</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Son Ödeme</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Ödeme Tarihi</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Durum</TableCell>
              <TableCell align="right" sx={{ color: '#a3a3a3', fontWeight: 500 }}>İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dues.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#a3a3a3' }}>
                  Kayıt bulunamadı
                </TableCell>
              </TableRow>
            ) : (
              dues.map((due) => {
                const chipSx = getDuesStatusChipSx(due.status);
                return (
                  <TableRow
                    key={due.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/payments/dues/${due.id}`)}
                  >
                    <TableCell>{due.memberName || '-'}</TableCell>
                    <TableCell>{due.period}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{formatDuesMoney(due.amount, due.currency)}</TableCell>
                    <TableCell>{formatDuesDate(due.dueDate)}</TableCell>
                    <TableCell>{formatDuesDate(due.finalDueDate)}</TableCell>
                    <TableCell>{formatDuesDate(due.paymentDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={due.statusName || DuesStatusNames[due.status]}
                        size="small"
                        sx={{ height: 24, fontSize: '11px', fontWeight: 500, borderRadius: '12px', ...chipSx }}
                      />
                      {due.isOverdue && due.status !== DuesStatus.Overdue && (
                        <Chip label="Gecikmiş" size="small" sx={{ ml: 0.5, height: 22, fontSize: '10px', bgcolor: '#FFEEF3', color: '#F8285A' }} />
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Detay">
                        <IconButton size="small" onClick={() => navigate(`/payments/dues/${due.id}`)}>
                          <VisibilityIcon sx={{ fontSize: 18, color: '#0E4D94' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" size="small" />
        </Box>
      )}

      <CreateMemberDuesDialog
        open={createSingleOpen}
        onClose={() => setCreateSingleOpen(false)}
        onCreated={() => loadDues()}
      />
      <BulkCreateDuesDialog
        open={createBulkOpen}
        onClose={() => setCreateBulkOpen(false)}
        onCompleted={() => loadDues()}
      />

      <Dialog open={policyDialogOpen} onClose={() => setPolicyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', pr: 3 }}>
          Aidat Politikası
          <IconButton onClick={() => setPolicyDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Politika yalnızca varsayılan tutar ve vade önerisi sağlar. Aidat kesimi formlardan yapılır.
          </Alert>
          {policyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : policy ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Politika Adı" value={policy.name || ''} onChange={(e) => setPolicy({ ...policy, name: e.target.value })} fullWidth />
              <TextField label="Tutar" type="number" value={policy.amount || 0} onChange={(e) => setPolicy({ ...policy, amount: parseFloat(e.target.value) || 0 })} fullWidth />
              <TextField
                label="Para Birimi"
                value={getCurrencyDisplayLabel(policy.currency || 'TRY')}
                onChange={(e) => setPolicy({
                  ...policy,
                  currency: normalizeCurrencyInput(e.target.value) || 'TRY',
                })}
                fullWidth
              />
              <TextField select label="Faturalama Periyodu" value={policy.billingPeriod || 1} onChange={(e) => setPolicy({ ...policy, billingPeriod: parseInt(e.target.value, 10) })} fullWidth>
                {Object.entries(BillingPeriodNames).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </TextField>
              <TextField label="Vade Günü" type="number" value={policy.dueDay || 1} onChange={(e) => setPolicy({ ...policy, dueDay: parseInt(e.target.value, 10) || 1 })} fullWidth inputProps={{ min: 1, max: 31 }} />
              <TextField label="Ek Süre (Gün)" type="number" value={policy.gracePeriodDays || 0} onChange={(e) => setPolicy({ ...policy, gracePeriodDays: parseInt(e.target.value, 10) || 0 })} fullWidth />
              <TextField label="Açıklama" value={policy.description || ''} onChange={(e) => setPolicy({ ...policy, description: e.target.value })} fullWidth multiline rows={3} />
            </Stack>
          ) : (
            <Typography color="text.secondary">Politika yüklenemedi</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPolicyDialogOpen(false)} sx={{ textTransform: 'none' }}>İptal</Button>
          <Button variant="contained" onClick={handleUpdatePolicy} disabled={policyLoading} sx={{ textTransform: 'none', bgcolor: '#0E4D94' }}>Kaydet</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dues;
